import type { RoofModel, RoofFace, PanelInfo, PanelLayout, ProductionResult, SolarModule } from '../types';

// ============================================================
// GOOGLE SOLAR API — Real building roof geometry + panel layout
// https://developers.google.com/maps/documentation/solar
//
// Uses buildingInsights:findClosest to get REAL data for a REAL
// building: roof segment pitch/azimuth/area (from aerial + LiDAR),
// Google's own optimal panel placement (real lat/lng per panel),
// and real annual production (their own sunshine simulation).
//
// Coverage is not universal — returns null on 404 (no data for
// this location), which the caller uses to fall back to
// Trace Roof or the simulated roof model.
// ============================================================

const SOLAR_API_URL = 'https://solar.googleapis.com/v1/buildingInsights:findClosest';

interface LatLng {
  latitude: number;
  longitude: number;
}

interface LatLngBox {
  sw: LatLng;
  ne: LatLng;
}

interface SizeAndSunshineStats {
  areaMeters2: number;
  sunshineQuantiles?: number[];
  groundAreaMeters2: number;
}

interface RoofSegmentStat {
  pitchDegrees: number;
  azimuthDegrees: number;
  stats: SizeAndSunshineStats;
  center: LatLng;
  boundingBox: LatLngBox;
  planeHeightAtCenterMeters: number;
}

interface SolarPanelRaw {
  center: LatLng;
  orientation: 'LANDSCAPE' | 'PORTRAIT';
  yearlyEnergyDcKwh: number;
  segmentIndex: number;
}

interface RoofSegmentSummary {
  pitchDegrees: number;
  azimuthDegrees: number;
  panelsCount: number;
  yearlyEnergyDcKwh: number;
  segmentIndex: number;
}

interface SolarPanelConfig {
  panelsCount: number;
  yearlyEnergyDcKwh: number;
  roofSegmentSummaries: RoofSegmentSummary[];
}

interface BuildingInsightsResponse {
  name: string;
  center: LatLng;
  boundingBox: LatLngBox;
  solarPotential: {
    maxArrayPanelsCount: number;
    panelCapacityWatts: number;
    panelHeightMeters: number;
    panelWidthMeters: number;
    wholeRoofStats: SizeAndSunshineStats;
    roofSegmentStats: RoofSegmentStat[];
    solarPanels: SolarPanelRaw[];
    solarPanelConfigs: SolarPanelConfig[];
  };
  imageryQuality: 'HIGH' | 'MEDIUM' | 'BASE';
}

export interface SolarApiResult {
  roofModel: RoofModel;
  panelLayout: PanelLayout;
  production: ProductionResult;
  imageryQuality: string;
  maxArrayPanelsCount: number;
}

/** Result codes so the caller can show the right message / trigger the right fallback. */
export type SolarApiOutcome =
  | { ok: true; data: SolarApiResult }
  | { ok: false; reason: 'no_coverage' | 'no_key' | 'error'; message: string };

function getApiKey(): string {
  return (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || '';
}

// --- Coordinate math (mirrors satellite.ts, lat/lon -> feet offset from center) ---

function latLonToFeetOffset(lat: number, lon: number, centerLat: number, centerLon: number) {
  const metersPerDegLat = 111320;
  const metersPerDegLon = 111320 * Math.cos((centerLat * Math.PI) / 180);
  const dLat = lat - centerLat;
  const dLon = lon - centerLon;
  const yMeters = dLat * metersPerDegLat;
  const xMeters = dLon * metersPerDegLon;
  return { x: xMeters * 3.28084, y: yMeters * 3.28084 };
}

/**
 * Approximate a roof segment's 2D polygon from its lat/lng bounding box,
 * rotated to align with its azimuth. Google doesn't give exact polygon
 * vertices for a segment — only a bounding box + pitch + azimuth — so we
 * build a rectangle sized to match the segment's real area, centered at
 * the segment's real center point. This is the standard approach used by
 * most Solar API integrations for segments that aren't simple gables.
 */
function segmentToPolygon(
  seg: RoofSegmentStat,
  centerLat: number,
  centerLon: number
): [number, number][] {
  const centerFeet = latLonToFeetOffset(seg.center.latitude, seg.center.longitude, centerLat, centerLon);

  // Ground footprint area in sq ft (undo the pitch tilt to get footprint, then re-derive a
  // width/depth pair using the bounding box aspect ratio for a reasonable rectangle shape).
  const groundAreaSqFt = seg.stats.groundAreaMeters2 * 10.7639;

  const nw = latLonToFeetOffset(seg.boundingBox.ne.latitude, seg.boundingBox.sw.longitude, centerLat, centerLon);
  const se = latLonToFeetOffset(seg.boundingBox.sw.latitude, seg.boundingBox.ne.longitude, centerLat, centerLon);
  const bboxWidth = Math.abs(se.x - nw.x);
  const bboxDepth = Math.abs(nw.y - se.y);

  // Scale the bounding box down so its area matches the real ground area
  // (bounding boxes over-estimate area for non-rectangular / rotated segments).
  const bboxArea = bboxWidth * bboxDepth;
  const scale = bboxArea > 0 ? Math.sqrt(groundAreaSqFt / bboxArea) : 1;
  const halfW = (bboxWidth * scale) / 2;
  const halfD = (bboxDepth * scale) / 2;

  const cx = centerFeet.x;
  const cy = centerFeet.y;

  return [
    [cx - halfW, cy - halfD],
    [cx + halfW, cy - halfD],
    [cx + halfW, cy + halfD],
    [cx - halfW, cy + halfD],
  ];
}

/**
 * Fetch real building insights for a location and map the response into
 * our internal RoofModel / PanelLayout / ProductionResult shapes.
 *
 * Panel placement uses Google's own solarPanels[] positions (Option A):
 * these are the exact panels Google's algorithm placed on the real roof,
 * with real per-panel yearly energy. We use the largest available config
 * (max panel count) as the default layout.
 */
export async function fetchSolarApiDesign(lat: number, lon: number): Promise<SolarApiOutcome> {
  const apiKey = getApiKey();
  if (!apiKey) {
    return { ok: false, reason: 'no_key', message: 'Google Solar API key is not configured.' };
  }

  const params = new URLSearchParams({
    'location.latitude': lat.toFixed(5),
    'location.longitude': lon.toFixed(5),
    requiredQuality: 'MEDIUM',
    key: apiKey,
  });

  let resp: Response;
  try {
    resp = await fetch(`${SOLAR_API_URL}?${params.toString()}`);
  } catch {
    return { ok: false, reason: 'error', message: 'Network error contacting Google Solar API.' };
  }

  if (resp.status === 404) {
    return { ok: false, reason: 'no_coverage', message: 'No Google Solar data is available for this address.' };
  }
  if (!resp.ok) {
    let msg = `Google Solar API error (${resp.status}).`;
    try {
      const errBody = await resp.json();
      if (errBody?.error?.message) msg = errBody.error.message;
    } catch {
      // ignore
    }
    return { ok: false, reason: 'error', message: msg };
  }

  const body = (await resp.json()) as BuildingInsightsResponse;
  const sp = body.solarPotential;
  if (!sp || !sp.roofSegmentStats?.length) {
    return { ok: false, reason: 'no_coverage', message: 'Google Solar data has no usable roof segments for this address.' };
  }

  const centerLat = body.center.latitude;
  const centerLon = body.center.longitude;

  // Precompute once (avoid recomputing min height inside the per-segment map below).
  const minSegHeightFeet = Math.min(...sp.roofSegmentStats.map(s => s.planeHeightAtCenterMeters * 3.28084));

  // Reference for normalizing sunshineQuantiles into a 0-100 solar access:
  // the best segment's ~90th-percentile flux (avoids single-point outliers).
  const segmentHighs = sp.roofSegmentStats.map(s => {
    const q = s.stats.sunshineQuantiles;
    return q && q.length >= 2 ? q[q.length - 2] : 0;
  });
  const maxHighFlux = Math.max(...segmentHighs);
  let usedQuantiles = false;

  // --- Map roof segments -> RoofFace[] ---
  const faces: RoofFace[] = sp.roofSegmentStats.map((seg, idx) => {
    const polygon = segmentToPolygon(seg, centerLat, centerLon);
    const areaSqFt = seg.stats.areaMeters2 * 10.7639;
    const heightFeet = seg.planeHeightAtCenterMeters * 3.28084;

    // Solar access from Google's real per-segment sunshineQuantiles
    // (kWh/kW flux distribution): median flux relative to the best
    // segment's high flux. Falls back to the old azimuth heuristic
    // when quantiles are missing.
    let solarAccess: number;
    const quantiles = seg.stats.sunshineQuantiles;
    if (quantiles && quantiles.length >= 3 && maxHighFlux > 0) {
      const median = quantiles[Math.floor(quantiles.length / 2)];
      solarAccess = Math.max(30, Math.min(100, Math.round((median / maxHighFlux) * 100)));
      usedQuantiles = true;
    } else {
      const southDiff = Math.min(Math.abs(seg.azimuthDegrees - 180), 360 - Math.abs(seg.azimuthDegrees - 180));
      solarAccess = Math.max(30, Math.min(95, Math.round(90 - (southDiff / 180) * 45)));
    }

    return {
      id: `gseg_${idx}`,
      label: `Roof Segment ${idx + 1}`,
      azimuth: seg.azimuthDegrees,
      pitch: seg.pitchDegrees,
      vertices: polygon,
      height: Math.max(8, heightFeet - minSegHeightFeet) + 10,
      area_sqft: areaSqFt,
      solar_access: solarAccess,
    };
  });

  // --- Pick the largest available panel configuration ---
  const configs = sp.solarPanelConfigs || [];
  const bestConfig = configs.length > 0 ? configs[configs.length - 1] : null;
  const panelCount = bestConfig ? bestConfig.panelsCount : 0;
  const wattage = sp.panelCapacityWatts || 400;

  // solarPanels[] is ordered by production; take the first N matching bestConfig's count
  const rawPanels = (sp.solarPanels || []).slice(0, panelCount || sp.solarPanels?.length || 0);

  const panels: PanelInfo[] = rawPanels.map((p, i) => {
    const feet = latLonToFeetOffset(p.center.latitude, p.center.longitude, centerLat, centerLon);
    const face = faces[p.segmentIndex] || faces[0];
    return {
      id: `gpanel_${i}`,
      face_id: face.id,
      position: [feet.x, feet.y, face.height],
      orientation: p.orientation === 'LANDSCAPE' ? 'landscape' : 'portrait',
      wattage,
    };
  });

  const module: SolarModule = {
    id: 'google_default',
    manufacturer: 'Google Solar API',
    model: `${wattage}W Reference Panel`,
    wattage,
    efficiency: 20.0,
    length: sp.panelHeightMeters || 1.879,
    width: sp.panelWidthMeters || 1.045,
  };

  const totalWattage = panels.length * wattage;
  const panelLayout: PanelLayout = {
    panels,
    total_count: panels.length,
    total_system_kw: totalWattage / 1000,
    equipment: { module },
  };

  const annualKwh = bestConfig ? Math.round(bestConfig.yearlyEnergyDcKwh) : 0;
  // Distribute annual production across months using a standard US seasonal curve
  // (Google's API doesn't return a monthly breakdown, only annual per-config).
  const monthlyWeights = [0.06, 0.07, 0.08, 0.09, 0.10, 0.11, 0.11, 0.10, 0.09, 0.08, 0.06, 0.05];
  const monthly = monthlyWeights.map(w => Math.round(annualKwh * w));

  const production: ProductionResult = {
    annual_kwh: annualKwh,
    monthly_kwh: monthly,
    system_losses_pct: 14,
    offset_pct: null,
    source: 'google_solar',
  };

  const roofWidthFeet = Math.max(...faces.flatMap(f => f.vertices.map(v => v[0]))) - Math.min(...faces.flatMap(f => f.vertices.map(v => v[0])));
  const roofDepthFeet = Math.max(...faces.flatMap(f => f.vertices.map(v => v[1]))) - Math.min(...faces.flatMap(f => f.vertices.map(v => v[1])));
  const avgPitch = faces.reduce((s, f) => s + f.pitch, 0) / faces.length;
  const maxHeight = Math.max(...faces.map(f => f.height));

  const roofModel: RoofModel = {
    faces,
    obstructions: [], // Solar API does not expose discrete obstruction geometry
    roofWidth: roofWidthFeet,
    roofDepth: roofDepthFeet,
    roofHeight: maxHeight,
    pitchDeg: avgPitch,
    source: 'google_solar',
    shading_basis: usedQuantiles ? 'measured' : 'heuristic',
  };

  return {
    ok: true,
    data: {
      roofModel,
      panelLayout,
      production,
      imageryQuality: body.imageryQuality,
      maxArrayPanelsCount: sp.maxArrayPanelsCount,
    },
  };
}
